import {
  put, call, select, all,
} from 'lib/redux';
import * as types from 'constants/ActionTypes';
import getOrderHistoryReportDetailsService from 'services/getOrderHistoryReportDetails.service';
import getOrderHistoryReportIDsService from 'services/getOrderHistoryReportIDs.service';
import orderHistoryPropertyDataService from 'services/orderHistoryPropertyData.service';
import getSubscriptionReportDetailsService from 'services/getSubscriptionReportDetails.service';
import { SET_PAGE_OPTIONS } from 'constants/ActionTypes';
import { getInitParametersSaga, getProductCatalogSaga, getOrgDescendantsSaga } from 'features/userData/UserData.saga';
import getOrderHistoryReportFileTypesService from 'services/getOrderHistoryReportFileTypes.service';
import getAvailableReportActionsService from 'services/getAvailableReportActions.service';
import { fetchOrderClosureReasonsSaga } from 'features/cancel-order-dialog/CancelOrder.saga';
import {
  CLIENT_ERROR_RESPONSES, STATUSES,
} from 'constants/AppConstants';
import {
  isEntitledForGlobalSearchSelector,
  isSubscriptionDashboardV2EnabledSelector,
} from 'utils/appSelectors.utils';
import {
  parseDateFromSearchText,
  getOrderHistoryFilterDates,
} from 'utils/date.utils';
import { logError, logWarning } from 'utils/logger.utils';
import {
  canEditPitches,
  getAllOrgIds,
  getFileTypesObjectFromList,
  getPageOptionsFromURLParams,
  getProductsList,
  expandProductIdsWithWallsAddOns, // #genai
  getReportIDsForPage,
  getSearchQueryFromURLParams,
  isCompletedOrReadyToCapturePayment,
  isReportCancellable,
  isReportSent,
  setPageOptionsToURLParams,
  setQueryValuesToURLParams,
} from './OrderHistory.utils';
import { STATUS_FILTER_VALUES, SUB_STATUSES } from './OrderHistory.constants';

export function* fetchOrderHistoryReportIdsSaga(searchText, top) {
  try {
    const { customerId: userId, orgNodeId, rootOrgNodeId } = yield select(({ userData: { initParameters } }) => initParameters);

    const isEntitledForGlobalSearch = yield select(isEntitledForGlobalSearchSelector);

    const {
      startDate,
      endDate,
      statusList,
      productIds,
      orgNodeList,
    } = yield select(({ orderHistory }) => orderHistory.orderHistoryAppliedFilters);

    const [appliedStartDate, appliedEndDate] = getOrderHistoryFilterDates(startDate, endDate);

    const response = yield call(
      getOrderHistoryReportIDsService,
      {
        userIdList: (orgNodeList?.length > 0) ? [] : [userId],
        orgNodeId: isEntitledForGlobalSearch ? rootOrgNodeId : orgNodeId,
        ...(searchText ? { searchText: parseDateFromSearchText(searchText) } : {}),
        ...(top ? { top } : {}),
        startDate: appliedStartDate,
        endDate: appliedEndDate,
        statusList: statusList?.reduce((acc, status) => [...acc, ...(STATUS_FILTER_VALUES[status] || [])], []),
        // #genai include Walls add-on PIDs so reports with Walls as add-on match
        productIds: expandProductIdsWithWallsAddOns(productIds),
        orgNodeList: orgNodeList.filter((orgId) => orgId > 0),
      },
    );
    if (!response.ok) {
      const error = new Error(response.json.errorDescription);
      error.errorCode = response.json.errorCode;
      throw error;
    }
    const { reportIds } = response.json;
    yield put({ type: types.ORDER_HISTORY_REPORT_IDS_SUCCESS, reportIds });
  } catch (error) {
    logError('Error in fetchOrderHistoryReportIdsSaga', {
      error: error.message,
      errorCode: error.errorCode,
      searchText,
      top,
    });
    yield put({ type: types.LOAD_ORDER_HISTORY_ERROR, error: error.message });
  }
}

export function* fetchOrderHistoryReportDetailsSaga(reportIDsForPage) {
  try {
    const { companyId, orgNodeId } = yield select(({ userData: { initParameters } }) => initParameters);

    const response = yield call(getOrderHistoryReportDetailsService, reportIDsForPage, companyId, orgNodeId);
    if (!response.ok) {
      if (response.status === CLIENT_ERROR_RESPONSES.STATUS_NOT_FOUND) {
        logWarning('Report details not found', { reportIDsForPage, companyId, orgNodeId });
        yield put({ type: types.REPORT_DETAILS_NOT_FOUND });
      }
      const error = new Error(response.json.errorDescription);
      error.errorCode = response.json.errorCode;
      throw error;
    }
    const reportDetails = response.json;
    yield put({ type: types.ORDER_HISTORY_REPORT_DETAILS_SUCCESS, reportDetails });
    return reportDetails;
  } catch (error) {
    logError('Error in fetchOrderHistoryReportDetailsSaga', {
      error: error.message,
      errorCode: error.errorCode,
      reportIDsForPage,
    });
    yield put({ type: types.LOAD_ORDER_HISTORY_ERROR, error: error.message });
    return [];
  }
}

export function* fetchOrderHistoryReportFileTypesSaga(reportDetails) {
  try {
    const reportIds = reportDetails
      .filter(({ statusID, subStatusID }) => isCompletedOrReadyToCapturePayment(statusID, subStatusID))
      .map(({ reportId }) => reportId);

    if (reportIds.length > 0) {
      const response = yield call(getOrderHistoryReportFileTypesService, reportIds);
      if (!response.ok) {
        const error = new Error(response.json.errorDescription);
        error.errorCode = response.json.errorCode;
        throw error;
      }
      const fileTypes = getFileTypesObjectFromList(response.json);
      yield put({ type: types.ORDER_HISTORY_REPORT_FILE_TYPES_SUCCESS, fileTypes });
    }
  } catch (error) {
    logError('Error in fetchOrderHistoryReportFileTypesSaga', {
      error: error.message,
      errorCode: error.errorCode,
      reportDetails,
    });
    yield put({ type: types.LOAD_ORDER_HISTORY_ERROR, error: error.message });
  }
}

export function* fetchOrderHistoryPropertyDataSaga(reportDetails) {
  try {
    if (!reportDetails || reportDetails.length === 0) {
      return;
    }

    yield put({ type: types.ORDER_HISTORY_PROPERTY_DATA_LOADING });

    const reportIds = reportDetails.map(({ reportId }) => reportId);
    const response = yield call(orderHistoryPropertyDataService, reportIds);

    if (!response.ok) {
      const error = new Error(response.json?.errorDescription || 'Failed to fetch property data');
      error.errorCode = response.json?.errorCode;
      throw error;
    }

    // Parse the response and create a map of reportId -> property data
    const propertyDataMap = {};
    if (response.json) {
      Object.keys(response.json).forEach((reportId) => {
        const reportData = response.json[reportId];
        // #genai: use optional chaining (Sonar S6582); reportData is either an object or undefined here, so semantics are unchanged
        if (reportData?.value) {
          try {
            // Parse the JSON string in the value field
            const parsedData = JSON.parse(reportData.value);
            // Filter out null/empty values
            const filteredData = Object.keys(parsedData).reduce((acc, key) => {
              if (parsedData[key] !== null && parsedData[key] !== undefined && parsedData[key] !== '') {
                acc[key] = parsedData[key];
              }
              return acc;
            }, {});
            propertyDataMap[reportId] = filteredData;
          } catch (parseError) {
            logWarning('Failed to parse property data', {
              reportId,
              parseError: parseError.message,
              value: reportData.value,
            });
          }
        }
      });
    }

    yield put({ type: types.ORDER_HISTORY_PROPERTY_DATA_SUCCESS, propertyDataMap });
  } catch (error) {
    logError('Error in fetchOrderHistoryPropertyDataSaga', {
      error: error.message,
      errorCode: error.errorCode,
      reportDetails: reportDetails?.length || 0,
    });
    yield put({ type: types.ORDER_HISTORY_PROPERTY_DATA_ERROR, error: error.message });
  }
}

export function* fetchSubscriptionReportDetailsSaga(reportIds) {
  try {
    if (!reportIds || reportIds.length === 0) {
      return;
    }

    const response = yield call(getSubscriptionReportDetailsService, reportIds);

    if (!response.ok) {
      const error = new Error(response.json?.errorDescription || 'Failed to fetch subscription report details');
      error.errorCode = response.json?.errorCode;
      throw error;
    }
    // Create a map of reportId -> subscription details
    const subscriptionDetailsMap = {};
    if (response.json && Array.isArray(response.json)) {
      response.json.forEach((detail) => {
        subscriptionDetailsMap[detail.reportId] = {
          subscriptionSku: detail.subscriptionSku,
          status: detail.status,
          individualCost: detail.individualCost,
          overageRate: detail.overageRate,
          integrationFee: detail.integrationFee,
          deliveryFee: detail.deliveryFee,
        };
      });
    }

    yield put({ type: types.ORDER_HISTORY_SUBSCRIPTION_DETAILS_SUCCESS, subscriptionDetailsMap });
  } catch (error) {
    logError('Error in fetchSubscriptionReportDetailsSaga', {
      error: error.message,
      errorCode: error.errorCode,
      reportIds: reportIds?.length || 0,
    });
  }
}

export function* getAvailableReportActions(reportDetails, allReportIds, availableActionsResponse) {
  try {
    const canEditReportIds = new Set(reportDetails
      .filter(({ statusID, subStatusID, primaryProductID }) => (
        canEditPitches(primaryProductID)
        && ((statusID === STATUSES.Completed && isReportSent(subStatusID)) || subStatusID === SUB_STATUSES.ReadyToCapturePayment)))
      .map(({ reportId }) => reportId));

    const enableTrueDesignLinkReportIds = new Set(reportDetails
      .filter(({ statusID, subStatusID }) => (isCompletedOrReadyToCapturePayment(statusID, subStatusID)))
      .map(({ reportId }) => reportId));

    const availableActions = allReportIds.reduce((actions, reportId) => {
      const reportActions = availableActionsResponse[reportId];
      return ({
        ...actions,
        [reportId]: {
          canEdit: Boolean(reportActions) && reportActions.canEdit && canEditReportIds.has(reportId),
          enableTrueDesignLink: Boolean(reportActions) && reportActions.enableTrueDesignLink && enableTrueDesignLinkReportIds.has(reportId),
          canUpgradeReport: Boolean(reportActions) && reportActions.canUpgradeReport,
          canAddESX: Boolean(reportActions) && reportActions.canAddESX,
        },
      });
    }, {});

    yield put({ type: types.AVAILABLE_REPORT_ACTIONS_SUCCESS, availableActions });
  } catch (error) {
    logError('Error in getAvailableReportActions', {
      error: error.message,
      errorCode: error.errorCode,
      reportDetails,
      allReportIds,
    });
    yield put({ type: types.LOAD_ORDER_HISTORY_ERROR, error: error.message });
  }
}

export function* fetchAvailableReportActions(reportIDsForPage) {
  try {
    const {
      ok: availableActionsResponseStatus,
      json: availableActionsResponse,
    } = yield call(getAvailableReportActionsService, reportIDsForPage);

    if (!availableActionsResponseStatus) {
      const error = new Error(availableActionsResponse.errorDescription);
      error.errorCode = availableActionsResponse.errorCode;
      throw error;
    }
    return availableActionsResponse;
  } catch (error) {
    logError('Error in fetchAvailableReportActions', {
      error: error.message,
      errorCode: error.errorCode,
      reportIDsForPage,
    });
    throw error;
  }
}

export function* fetchOrderHistoryPageDataSaga(reportIDsForPage) {
  try {
    if (reportIDsForPage.length > 0) {
      const { reportDetails, availableActions } = yield all({
        availableActions: fetchAvailableReportActions(reportIDsForPage),
        reportDetails: fetchOrderHistoryReportDetailsSaga(reportIDsForPage),
      });

      const isSubscriptionDashboardV2Enabled = yield select(isSubscriptionDashboardV2EnabledSelector);

      yield all([
        fetchOrderHistoryReportFileTypesSaga(reportDetails),
        fetchOrderHistoryPropertyDataSaga(reportDetails),
        ...(isSubscriptionDashboardV2Enabled
          ? [fetchSubscriptionReportDetailsSaga(reportIDsForPage)] : []),
        ...(reportDetails.some(({ statusID, subStatusID }) => isReportCancellable(statusID, subStatusID))
          ? [fetchOrderClosureReasonsSaga()] : []),
        availableActions && getAvailableReportActions(reportDetails, reportIDsForPage, availableActions),
      ]);
      yield put({ type: types.LOADED_ORDER_HISTORY_REPORTS });
    } else {
      yield put({ type: types.LOADED_ORDER_HISTORY_REPORTS });
    }
  } catch (error) {
    logError('Error in fetchOrderHistoryPageDataSaga', {
      error: error.message,
      errorCode: error.errorCode,
      reportIDsForPage,
    });
    yield put({ type: types.LOAD_ORDER_HISTORY_ERROR, error: error.message });
  }
}

function* setPageOptionsSaga(pageOptions) {
  setPageOptionsToURLParams(pageOptions);

  yield put({ type: SET_PAGE_OPTIONS, pageOptions });
}

export function* updatePageOptionsSaga({ pageOptions }) {
  try {
    yield put({ type: types.LOADING_ORDER_HISTORY_REPORTS });
    yield put({ type: types.CLEAR_PAGE_DATA });
    yield call(setPageOptionsSaga, pageOptions);

    const {
      reportIds, pageNumber, pageSize,
    } = yield select(({ orderHistory }) => orderHistory);

    const reportIDsForPage = getReportIDsForPage(reportIds, pageNumber, pageSize);
    yield call(fetchOrderHistoryPageDataSaga, reportIDsForPage);
  } catch (error) {
    logError('Error in updatePageOptionsSaga', {
      error: error.message,
      errorCode: error.errorCode,
      pageOptions,
    });
    yield put({ type: types.LOAD_ORDER_HISTORY_ERROR, error: error.message });
  }
}

export function* updateOrgNodesListForGlobalSearchSaga() {
  try {
    yield call(getOrgDescendantsSaga);
    const { orgDescendants } = yield select(({ userData }) => userData);
    const allOrgIds = getAllOrgIds(orgDescendants);
    yield put({ type: types.ORDER_HISTORY_FILTERS_UPDATE_ORG_NODES_LIST, updatedOrgNodeList: allOrgIds });
    yield put({ type: types.ORDER_HISTORY_SET_APPLIED_FILTERS });
  } catch (error) {
    logError('Error in updateOrgNodesListForGlobalSearchSaga', {
      error: error.message,
      errorCode: error.errorCode,
    });
    yield put({ type: types.LOAD_ORDER_HISTORY_ERROR, error: error.message });
  }
}

export function* fetchOrderHistoryDataSaga({ searchText } = {}) {
  try {
    const search = searchText === undefined ? getSearchQueryFromURLParams() : searchText;

    const { productIds } = yield select(({ orderHistory }) => orderHistory.orderHistoryAppliedFilters);
    const selectedProducts = new URLSearchParams(window.location.search).get('product_ids')?.split(',').map(Number) ?? productIds;

    if (selectedProducts.length > 0) {
      // #genai validate deep-linked product_ids against broader catalog (incl. internals)
      const allProductIds = new Set(getProductsList(yield select(({ userData }) => userData.productCatalog)));
      // #genai Roof 3D and Roof 2D are independent filter options; de-dupe repeated IDs from legacy links
      const productsList = [...new Set(selectedProducts)].filter((product) => allProductIds.has(product));
      yield put({ type: types.ORDER_HISTORY_FILTERS_UPDATE_PRODUCTS_LIST, productsList });
      setQueryValuesToURLParams('product_ids', productsList);
      yield put({ type: types.ORDER_HISTORY_SET_APPLIED_FILTERS });
    }

    setQueryValuesToURLParams('search', search);
    yield put({ type: types.UPDATE_SEARCH_TEXT, searchText: search || '' });
    yield call(fetchOrderHistoryReportIdsSaga, search);

    const reportIds = yield select(({ orderHistory }) => orderHistory.reportIds);
    yield call(setPageOptionsSaga, searchText ? { pageNumber: 1, pageSize: 10 } : getPageOptionsFromURLParams(reportIds));

    const { pageNumber, pageSize } = yield select(({ orderHistory }) => orderHistory);
    const reportIDsForPage = getReportIDsForPage(reportIds, pageNumber, pageSize);
    yield call(fetchOrderHistoryPageDataSaga, reportIDsForPage);
  } catch (error) {
    logError('Error in fetchOrderHistoryDataSaga', {
      error: error.message,
      errorCode: error.errorCode,
      searchText,
    });
    yield put({ type: types.LOAD_ORDER_HISTORY_ERROR, error: error.message });
  }
}

export function* applyFiltersSaga({ reset }) {
  try {
    const isEntitledForGlobalSearch = yield select(isEntitledForGlobalSearchSelector);

    yield put({ type: types.LOADING_ORDER_HISTORY_REPORTS });
    yield put({ type: types.CLEAR_REPORTS_DATA });

    if (reset) {
      yield put({ type: types.ORDER_HISTORY_RESET_FILTERS });
      if (isEntitledForGlobalSearch) {
        const { orgDescendants } = yield select(({ userData }) => userData);
        const allOrgIds = getAllOrgIds(orgDescendants);
        yield put({ type: types.ORDER_HISTORY_FILTERS_UPDATE_ORG_NODES_LIST, updatedOrgNodeList: allOrgIds });
        yield put({ type: types.ORDER_HISTORY_SET_APPLIED_FILTERS });
      }
    } else {
      yield put({ type: types.ORDER_HISTORY_SET_APPLIED_FILTERS });
    }

    const { productIds } = yield select(({ orderHistory }) => orderHistory.orderHistoryAppliedFilters);
    setQueryValuesToURLParams('product_ids', productIds);

    yield call(getInitParametersSaga);
    yield call(fetchOrderHistoryDataSaga);
  } catch (error) {
    logError('Error in applyFiltersSaga', {
      error: error.message,
      errorCode: error.errorCode,
      reset,
    });
    yield put({ type: types.LOAD_ORDER_HISTORY_ERROR, error: error.message });
  }
}

export function* loadOrderHistorySaga({ searchText } = {}) {
  try {
    const myevRefreshFiltersFeatureFlag = yield select(({ featureFlag }) => featureFlag.myevRefreshFilters);

    yield put({ type: types.ORDER_RESET_ERROR });
    yield put({ type: types.LOADING_ORDER_HISTORY_REPORTS });
    yield put({ type: types.CLEAR_REPORTS_DATA });

    yield call(getInitParametersSaga);

    if (myevRefreshFiltersFeatureFlag) yield call(getProductCatalogSaga);

    const isEntitledForGlobalSearch = yield select(isEntitledForGlobalSearchSelector);
    if (isEntitledForGlobalSearch) yield call(updateOrgNodesListForGlobalSearchSaga);

    yield call(fetchOrderHistoryDataSaga, { searchText });
  } catch (error) {
    logError('Error in loadOrderHistorySaga', {
      error: error.message,
      errorCode: error.errorCode,
      searchText,
    });
    yield put({ type: types.LOAD_ORDER_HISTORY_ERROR, error: error.message });
  }
}
